import {
  Box,
  Button,
  Flex,
  Skeleton,
  Spacer,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { InvoiceFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { InvoiceDisbursements } from './disbursements/InvoiceDisbursements';
import { InvoiceDetailSideDrawer } from './invoiceDetail/InvoiceDetailSideDrawer';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { getInvoiceComputedStatus } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { useGetInvoicesByLoanIdQuery } from 'packages/invoices/requests/invoices.gql.generated';
import { InvoicePaymentInKindItems } from 'packages/invoices/ui/paymentInKind/InvoicePaymentInKindItems';

const InvoiceSnapshotTag = ({
  icon,
  label,
  tooltip,
}: {
  icon: string;
  label: string;
  tooltip?: string;
}) => {
  return (
    <Tooltip label={tooltip} placement="top">
      <Tag
        borderRadius="64px"
        p="8px 16px"
        borderColor={colors.gray[100]}
        borderWidth="1px"
        backgroundColor="white"
        cursor="default"
      >
        <Icon icon={icon} />
        <Text ml="8px" fontSize="14px" fontWeight={500}>
          {label}
        </Text>
      </Tag>
    </Tooltip>
  );
};

const InvoiceSnapshot = ({ invoice }: { invoice: InvoiceFragmentGQLType }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const computedStatus = getInvoiceComputedStatus({
    status: invoice.status,
    dueDate: invoice.dueDate,
    targetInvoiceDate: invoice.targetInvoiceDate ?? undefined,
  });

  return (
    <Box
      p="40px"
      borderRadius="8px"
      borderColor={colors.gray[100]}
      borderWidth="1px"
      w="100%"
    >
      <Flex alignItems="center" justifyContent="space-between" w="100%">
        <Typography variant="section-subtitle" trim>
          Invoice
        </Typography>
        <Button
          colorScheme="blue"
          ml="1rem"
          mr=".5rem"
          onClick={onOpen}
          variant="link"
        >
          <Typography
            colorScheme="blue"
            fontWeight="medium"
            variant="base-muted"
          >
            {truncateUuid(invoice.id)}
          </Typography>
        </Button>
        <InvoiceStatusBadge
          computedStatus={computedStatus}
          useBackgroundColors={false}
        />
        <Spacer />
        <Flex alignItems="center" justifyContent="space-between" gap="24px">
          <InvoiceSnapshotTag
            icon="calendar-check"
            label={
              invoice.paymentDate
                ? commonDate.fullDateStrToHumanDateString(invoice.paymentDate)
                : '-'
            }
            tooltip="Payment Date"
          />
          <InvoiceSnapshotTag
            icon="calendar-clock"
            label={
              invoice.dueDate
                ? commonDate.fullDateStrToHumanDateString(invoice.dueDate)
                : '-'
            }
            tooltip="Due Date"
          />
          <InvoiceSnapshotTag
            icon="circle-dollar"
            label={dollarNumberWithCommas(invoice.totalAmount) ?? '-'}
            tooltip="Total Amount"
          />
        </Flex>
      </Flex>
      {invoice.status === 'paid' && invoice.invoiceType === 'loan' && (
        <VStack mt="40px" gap="40px" w="100%" alignItems="flex-start">
          <InvoiceDisbursements invoice={invoice} />
          <InvoicePaymentInKindItems invoice={invoice} />
        </VStack>
      )}
      <InvoiceDetailSideDrawer
        invoice={invoice}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  );
};

export const InvoiceSnapshotContainer = ({ loanId }: { loanId: string }) => {
  const {
    data: invoicesData,
    loading: isInvoicesLoading,
  } = useGetInvoicesByLoanIdQuery({
    variables: { loanId },
  });

  if (isInvoicesLoading || !invoicesData) {
    return (
      <Box
        p="40px"
        borderRadius="8px"
        borderColor={colors.gray[100]}
        borderWidth="1px"
        w="100%"
      >
        <Skeleton h="50px" w="100%" />
      </Box>
    );
  }

  if (invoicesData.invoices.length === 0) {
    return (
      <Box
        p="40px"
        borderRadius="8px"
        borderColor={colors.gray[100]}
        borderWidth="1px"
        w="100%"
      >
        No invoices found for this loan.
      </Box>
    );
  }

  return (
    <>
      {invoicesData.invoices.map((invoice) => (
        <InvoiceSnapshot invoice={invoice} key={invoice.id} />
      ))}
    </>
  );
};
